import {
  comparePeriods,
  describeComparison,
  hasComparableData,
  rankComparisons,
  type Entry,
  type ResolvedRange,
  type Track,
} from '@anytracker/core'

import { trackFill } from '../../charts/colours.js'
import styles from './Noticings.module.css'
import panel from './Comparison.module.css'

interface Props {
  readonly tracks: readonly Track[]
  /** Everything in both windows — the caller fetches the wider span. */
  readonly entries: readonly Entry[]
  readonly range: ResolvedRange
}

/**
 * This period against the one before it (T-1205).
 *
 * The comparison window is derived, not chosen: the equally long stretch
 * immediately before this one. Two freely picked ranges look more capable and
 * produce a worse answer — a count of 30 against a count of 14 is meaningless
 * and nothing on screen could warn the reader — so equal length by construction
 * removes the whole class of mistake.
 *
 * Every number and every sentence is core's, so this and the printed report
 * cannot disagree about either side.
 */
export function Comparison({ tracks, entries, range }: Props) {
  const comparison = comparePeriods(tracks, entries, range)
  if (!hasComparableData(comparison)) {
    return null
  }

  return (
    <section className={styles['section']} aria-labelledby="comparison-title" data-bleed>
      <h2 className={styles['title']} id="comparison-title">
        Against the {String(comparison.days)} days before
      </h2>

      {/*
        Absolute dates, never "the previous period". A reader coming back to this
        in six weeks has no way to know what a relative phrase meant — the same
        rule the report heading already follows.
      */}
      <p className={panel['window']} data-testid="comparison-window">
        {comparison.previous.label} · {String(comparison.previousEntries)} entries,
        against {String(comparison.currentEntries)} this time
      </p>

      <ul className={panel['rows']} data-testid="comparison-rows">
        {rankComparisons(comparison).map((one) => (
          <li key={one.track.id} className={panel['row']}>
            <span className={panel['name']}>
              <span
                className={panel['swatch']}
                style={{ background: trackFill(one.track) }}
                aria-hidden="true"
              />
              {one.track.name}
            </span>
            <span className={panel['change']}>
              {one.change !== null && one.change.direction !== 'level' && (
                <span className={panel['arrow']} aria-hidden="true">
                  {one.change.direction === 'up' ? '↑' : '↓'}
                </span>
              )}
              {describeComparison(one)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
